import React, { useEffect, useRef } from 'react';

export interface U2000CheckBoxBaseProps {
  fieldName: string;
  label: string;
  value?: boolean;
  setValue?: (value: boolean) => void;
  readonly?: boolean;
  className?: string;
  indeterminate?: boolean;
  /** Libellé accessible lorsque le texte visible est masqué (grille compacte). */
  ariaLabel?: string;
  /** Case seule, sans libellé visible à droite. */
  compact?: boolean;
}

const U2000CheckBoxBase: React.FC<U2000CheckBoxBaseProps> = ({
  fieldName,
  label,
  readonly = false,
  value,
  setValue,
  className,
  indeterminate = false,
  ariaLabel,
  compact = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  function handleCheck(checked: boolean) {
    setValue && setValue(!checked);
  }

  const disabled = readonly;
  const checked = value || false;
  const showLabel = !compact && label.trim() !== '';
  const accessibleName = ariaLabel?.trim() || (showLabel ? label : undefined);

  return (
    <div
      id={fieldName}
      className={`${compact ? 'relative inline-flex size-5 shrink-0 items-center justify-center' : 'flex h-full items-center'} ${compact || !showLabel ? 'justify-center gap-0' : 'gap-3'}${className ? ` ${className}` : ''}`}
    >
      <input
        ref={inputRef}
        id={`${fieldName}-checkbox`}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={() => !disabled && handleCheck(checked)}
        className={`peer cursor-pointer opacity-0 ${compact ? 'absolute inset-0 m-0 size-full' : 'absolute h-5 w-5'}`}
        aria-checked={indeterminate ? 'mixed' : checked}
        aria-disabled={disabled}
        aria-label={accessibleName}
      />
      <label
        htmlFor={`${fieldName}-checkbox`}
        className={`relative flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-all duration-200 cursor-pointer
          ${
            disabled
              ? 'cursor-not-allowed surface-border bg-muted'
              : indeterminate
                ? 'border-blue-500 bg-blue-500 hover:bg-blue-600 hover:border-blue-600'
                : checked
                  ? 'border-blue-500 bg-blue-500 hover:bg-blue-600 hover:border-blue-600'
                  : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-muted'
          }
          peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500/20 peer-focus-visible:ring-offset-2
        `}
      >
        {indeterminate ? (
          <span className={`block h-0.5 w-2.5 rounded-sm ${disabled ? 'bg-gray-400' : 'bg-white'}`} aria-hidden />
        ) : checked ? (
          <svg className={disabled ? 'text-gray-400' : 'text-white'} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" width="14" height="14">
            <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : null}
      </label>
      {showLabel ? (
        <label
          htmlFor={`${fieldName}-checkbox`}
          className={`flex h-full items-center text-sm font-medium cursor-pointer leading-relaxed ${disabled ? 'cursor-not-allowed text-gray-500' : 'text-gray-900 hover:text-gray-700'}`}
        >
          {label}
        </label>
      ) : null}
    </div>
  );
};

export default U2000CheckBoxBase;
